import React, { ErrorInfo, ReactNode } from "react";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  message: string;
  componentStack: string;
  errorStack: string;
};

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: false,
    message: "",
    componentStack: "",
    errorStack: "",
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      message: error.message || "Unexpected error",
      errorStack: error.stack || "",
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("React error:", error);
    console.error("Component stack:", info.componentStack);

    this.setState({
      componentStack: info.componentStack || "",
      errorStack: error.stack || "",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="mx-auto mt-20 max-w-xl rounded-xl bg-white p-5 shadow">
            <h1 className="text-xl font-bold">Something went wrong</h1>

            <p className="mt-3 rounded bg-red-50 p-3 text-red-600">
              {this.state.message}
            </p>

            <p className="mt-4 text-sm font-semibold">Component stack</p>

            <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-3 text-xs">
              {this.state.componentStack || "No component stack available"}
            </pre>

            <p className="mt-4 text-sm font-semibold">JavaScript stack</p>

            <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-3 text-xs">
              {this.state.errorStack || "No JavaScript stack available"}
            </pre>

            <button
              onClick={() => window.location.reload()}
              className="mt-5 w-full rounded bg-purple-600 py-3 font-semibold text-white"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
